import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { Box, Button, Container, Grid, Link, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import coursesCard from '@app/assets/courses-card2.jpg'
import familyEngagementCard from '@app/assets/family-engagement.jpg'
import knowledgeHubCard from '@app/assets/knowledgehub-card.jpg'
import { useAuth } from '@app/context/auth'
import { useKnowledgeHubUrl } from '@app/hooks/useKnowledgeHubUrl'
import theme from '@app/theme'

const GridItem: React.FC<
  React.PropsWithChildren<{
    image: string
    title: string
    icon: React.ReactNode
    url: string
    description: string
  }>
> = ({ image, title, description, children, icon, url }) => {
  return (
    <Box
      bgcolor="white"
      border={1}
      borderColor={theme.colors.lime[400]}
      borderRadius={3}
      component={Link}
      href={url}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        textDecoration: 'none',
      }}
      underline="none"
    >
      <Box sx={{ aspectRatio: '79 / 50' }}>
        <img
          alt={title}
          src={image}
          style={{
            height: '100%',
            maxWidth: '100%',
            objectFit: 'cover',
          }}
        />
      </Box>

      <Box
        p={3}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          color="primary"
          display="flex"
          fontFamily="Poppins"
          fontWeight={500}
          mb={2}
          variant="h4"
        >
          <Box component="span" mr={1} sx={{ color: 'grey' }}>
            {icon}
          </Box>

          {title}
        </Typography>

        <Typography mb={2}>{description}</Typography>
        <Box mt="auto">{children}</Box>
      </Box>
    </Box>
  )
}

export const AccountOrFamilyEngagementCard = () => {
  const {
    acl: { isUK },
  } = useAuth()
  const { t } = useTranslation('pages', {
    keyPrefix: 'welcome.membership-grid',
  })

  if (!isUK()) {
    return (
      <GridItem
        image={familyEngagementCard}
        title={t('fet-title')}
        description={t('fet-anz-description')}
        icon={<SettingsOutlinedIcon />}
        url={import.meta.env.VITE_ANZ_FET_ENROLL_FORM_URL}
      >
        <ul style={{ marginTop: '0px', marginBottom: '12px' }}>
          <li>
            <Typography> {t('fet-behaviour-and-the-brain')}</Typography>
          </li>
        </ul>
        <Button variant="contained" color="lime" fullWidth>
          {t('fet-anz-button')}
        </Button>
      </GridItem>
    )
  }
  return (
    <GridItem
      image={familyEngagementCard}
      title={t('fet-title')}
      description={t('fet-description')}
      icon={<SchoolOutlinedIcon />}
      url={import.meta.env.VITE_FAMILY_ENGAGEMENT_TRAINING}
    >
      <ul style={{ marginTop: '0px', marginBottom: '12px' }}>
        <li>
          <Link
            color="blue"
            href={import.meta.env.VITE_BEHAVIOUR_AND_THE_BRAIN_URL}
          >
            {t('fet-behaviour-and-the-brain')}
          </Link>
        </li>

        <li>
          <Link
            color="blue"
            href={import.meta.env.VITE_FAMILY_ENGAGEMENT_TRAINING_URL}
          >
            {t('fet-family-engagement-training')}
          </Link>
        </li>
      </ul>
      <Button variant="contained" color="lime" fullWidth>
        {t('fet-button')}
      </Button>
    </GridItem>
  )
}

export const MembershipGrid = () => {
  const {
    acl: { isAustralia },
    verified,
  } = useAuth()
  const knowledgeHubUrl = useKnowledgeHubUrl()
  const { t } = useTranslation('pages', {
    keyPrefix: 'welcome.membership-grid',
  })

  return (
    <Container>
      <Box sx={{ md: { width: '60%' } }} mb={5} mt={8}>
        <Typography
          variant="h4"
          fontWeight={500}
          color="primary"
          fontFamily="Poppins"
          mb={2}
        >
          {t('title')}
        </Typography>
        {isAustralia() && (
          <Typography lineHeight={1.75} sx={{ width: { md: '70%' } }}>
            {t('description')}
          </Typography>
        )}
      </Box>

      <Grid
        container
        spacing={4}
        sx={{
          '& .grid-item': {
            transition: 'opacity 0.4s',
          },
          ':hover': {
            '& .grid-item': { opacity: 0.5 },
            '& .grid-item:hover': { opacity: 1 },
          },
        }}
      >
        <Grid item md={4} className="grid-item">
          <GridItem
            image={knowledgeHubCard}
            title={t('knowledge-hub-title')}
            description={t('knowledge-hub-description')}
            icon={<AutoStoriesOutlinedIcon />}
            url={knowledgeHubUrl}
          >
            <Button color="lime" variant="contained" fullWidth>
              {t('knowledge-hub-button')}
            </Button>
          </GridItem>
        </Grid>
        <Grid item md={4} className="grid-item">
          <GridItem
            image={coursesCard}
            title={t('courses-title')}
            description={t('courses-description')}
            icon={<FormatListBulletedOutlinedIcon />}
            url={verified ? '/courses' : '/verify'}
          >
            <Button color="lime" variant="contained" fullWidth>
              {t('courses-button')}
            </Button>
          </GridItem>
        </Grid>
        <Grid item md={4} className="grid-item">
          <AccountOrFamilyEngagementCard />
        </Grid>
      </Grid>
    </Container>
  )
}
